import moment from 'moment'
import 'moment/locale/ko'

moment.updateLocale('ko', {
  week: {
    dow: 1,
  },
})

export const fmt = moment.HTML5_FMT.DATE

const emptyWeek = () => Array(7).fill(null)

export const maximum = (dateString1, dateString2) =>
  moment.max(moment(dateString1), moment(dateString2)).format(fmt)
export const minimum = (dateString1, dateString2) =>
  moment.min(moment(dateString1), moment(dateString2)).format(fmt)

export const getDays = month => {
  const start = month.clone().startOf('month')
  const end = month.clone().endOf('months')
  const current = start.clone()
  const result = []
  let week = emptyWeek()
  while (current.isSameOrBefore(end)) {
    week[current.day()] = current.clone()
    if (current.day() === 6 || current.isSame(end, 'days')) {
      result.push(week)
      week = emptyWeek()
    }
    current.add(1, 'days')
  }
  return result
}

export const CALENDAR_PRESETS = {
  TODAY: {
    key: 'TODAY',
    text: '오늘',
    duration: 1,
    unit: 'days',
    protocol: 'today',
    tiaraActionName: '캘린더_오늘_클릭',
  },
  YESTERDAY: {
    key: 'YESTERDAY',
    text: '어제',
    duration: 1,
    unit: 'days',
    protocol: 'yesterday',
    tiaraActionName: '캘린더_어제_클릭',
  },
  THIS_WEEK: {
    key: 'THIS_WEEK',
    text: '이번 주',
    duration: 1,
    unit: 'weeks',
    protocol: 'thisWeek',
    tiaraActionName: '캘린더_이번주_클릭',
  },
  LAST_WEEK: {
    key: 'LAST_WEEK',
    text: '지난 주',
    duration: 1,
    unit: 'weeks',
    protocol: 'lastWeek',
    tiaraActionName: '캘린더_지난주_클릭',
  },
  LAST_7_DAYS: {
    key: 'LAST_7_DAYS',
    text: '최근 7일',
    duration: 7,
    unit: 'days',
    protocol: 'last7Days',
    tiaraActionName: '캘린더_최근7일_클릭',
  },
  LAST_14_DAYS: {
    key: 'LAST_14_DAYS',
    text: '최근 14일',
    duration: 14,
    unit: 'days',
    protocol: 'last14Days',
    tiaraActionName: '캘린더_최근14일_클릭',
  },
  LAST_30_DAYS: {
    key: 'LAST_30_DAYS',
    text: '최근 30일',
    duration: 30,
    unit: 'days',
    protocol: 'last30Days',
    tiaraActionName: '캘린더_최근30일_클릭',
  },
  THIS_MONTH: {
    key: 'THIS_MONTH',
    text: '이번 달',
    duration: 1,
    unit: 'months',
    protocol: 'thisMonth',
    tiaraActionName: '캘린더_이번달_클릭',
  },
  LAST_MONTH: {
    key: 'LAST_MONTH',
    text: '지난 달',
    duration: 1,
    unit: 'months',
    protocol: 'lastMonth',
    tiaraActionName: '캘린더_지난달_클릭',
  },
  ALL: {
    key: 'ALL',
    text: '전체',
    duration: null,
    unit: null,
    protocol: 'all',
    tiaraActionName: '캘린더_전체_클릭',
  },
  CUSTOM: {
    key: 'CUSTOM',
    text: '맞춤 설정',
    duration: null,
    unit: 'days',
    protocol: null,
    tiaraActionName: '캘린더_맞춤설정_클릭',
  },
}

export const getVisibleMonth = valueString =>
  valueString
    ? moment(valueString).startOf('months')
    : moment().startOf('months')

export const findPresetByProtocol = protocol => {
  if (protocol) {
    const entry = Object.entries(CALENDAR_PRESETS).find(
      ([, value]) => value.protocol === protocol
    )
    if (entry) {
      const [key] = entry
      return CALENDAR_PRESETS[key]
    } else {
      return CALENDAR_PRESETS.CUSTOM
    }
  } else {
    return CALENDAR_PRESETS.CUSTOM
  }
}

export const getVisibleMonths = end => ({
  left: end.clone().startOf('month').subtract(1, 'months'),
  right: end.clone().startOf('month'),
})

export const getPresetDays = (min, max) => ({
  today: moment(),
  yesterday: moment().subtract(1, 'days'),
  thisWeekStart: min
    ? moment.max(moment().startOf('isoWeek'), min.clone())
    : moment().startOf('isoWeek'),
  thisWeekEnd: max
    ? moment.min(moment().endOf('isoWeek'), max.clone())
    : moment().endOf('isoWeek'),
  lastWeekStart:
    min &&
    min.isBetween(
      moment().subtract(1, 'isoWeek').startOf('isoWeek'),
      moment().subtract(1, 'isoWeek').endOf('isoWeek'),
      'days',
      '[]'
    )
      ? min
      : moment().subtract(1, 'isoWeek').startOf('isoWeek'),
  lastWeekEnd: moment().subtract(1, 'isoWeek').endOf('isoWeek'),
  thisMonthStart: min
    ? moment.max(moment().startOf('months'), min.clone())
    : moment().startOf('months'),
  thisMonthEnd: max
    ? moment.min(moment().endOf('months'), max.clone())
    : moment().endOf('months'),
  lastMonthStart:
    min &&
    min.isBetween(
      moment().subtract(1, 'months').startOf('months'),
      moment().subtract(1, 'months').endOf('months'),
      'days',
      '[]'
    )
      ? min
      : moment().subtract(1, 'months').startOf('months'),
  lastMonthEnd: moment().subtract(1, 'months').endOf('months'),
  last7daysStart: min
    ? moment.max(moment().subtract(6, 'days'), min.clone())
    : moment().subtract(6, 'days'),
  last14daysStart: min
    ? moment.max(moment().subtract(13, 'days'), min.clone())
    : moment().subtract(13, 'days'),
  last30daysStart: min
    ? moment.max(moment().subtract(29, 'days'), min.clone())
    : moment().subtract(29, 'days'),
})

export const getPreset = (start, end, min, max) => {
  const days = getPresetDays(min, max)
  if (start.isSame(days.today, 'days') && end.isSame(days.today, 'days')) {
    return CALENDAR_PRESETS.TODAY
  } else if (
    start.isSame(days.yesterday, 'days') &&
    end.isSame(days.yesterday, 'days')
  ) {
    return CALENDAR_PRESETS.YESTERDAY
  } else if (
    start.isSame(days.thisWeekStart, 'days') &&
    end.isSame(days.thisWeekEnd, 'days')
  ) {
    return CALENDAR_PRESETS.THIS_WEEK
  } else if (
    start.isSame(days.lastWeekStart, 'days') &&
    end.isSame(days.lastWeekEnd, 'days')
  ) {
    return CALENDAR_PRESETS.LAST_WEEK
  } else if (
    start.isSame(days.last7daysStart, 'days') &&
    end.isSame(days.today, 'days')
  ) {
    return CALENDAR_PRESETS.LAST_7_DAYS
  } else if (
    start.isSame(days.last14daysStart, 'days') &&
    end.isSame(days.today, 'days')
  ) {
    return CALENDAR_PRESETS.LAST_14_DAYS
  } else if (
    start.isSame(days.last30daysStart, 'days') &&
    end.isSame(days.today, 'days')
  ) {
    return CALENDAR_PRESETS.LAST_30_DAYS
  } else if (
    start.isSame(days.thisMonthStart, 'days') &&
    end.isSame(days.thisMonthEnd, 'days')
  ) {
    return CALENDAR_PRESETS.THIS_MONTH
  } else if (
    start.isSame(days.lastMonthStart, 'days') &&
    end.isSame(days.lastMonthEnd, 'days')
  ) {
    return CALENDAR_PRESETS.LAST_MONTH
  } else if (min && max && start.isSame(min) && end.isSame(max)) {
    return CALENDAR_PRESETS.ALL
  } else {
    return CALENDAR_PRESETS.CUSTOM
  }
}

export const getPresetRange = (preset, min, max) => {
  const days = getPresetDays(min, max)
  switch (preset) {
    case CALENDAR_PRESETS.TODAY:
      return {
        start: days.today,
        end: days.today,
      }
    case CALENDAR_PRESETS.YESTERDAY:
      return {
        start: days.yesterday,
        end: days.yesterday,
      }
    case CALENDAR_PRESETS.THIS_WEEK:
      return {
        start: days.thisWeekStart,
        end: days.thisWeekEnd,
      }
    case CALENDAR_PRESETS.LAST_WEEK:
      return {
        start: days.lastWeekStart,
        end: days.lastWeekEnd,
      }
    case CALENDAR_PRESETS.LAST_7_DAYS:
      return {
        start: days.last7daysStart,
        end: days.today,
      }
    case CALENDAR_PRESETS.LAST_14_DAYS:
      return {
        start: days.last14daysStart,
        end: days.today,
      }
    case CALENDAR_PRESETS.LAST_30_DAYS:
      return {
        start: days.last30daysStart,
        end: days.today,
      }
    case CALENDAR_PRESETS.THIS_MONTH:
      return {
        start: days.thisMonthStart,
        end: days.thisMonthEnd,
      }
    case CALENDAR_PRESETS.LAST_MONTH:
      return {
        start: days.lastMonthStart,
        end: days.lastMonthEnd,
      }
    case CALENDAR_PRESETS.ALL:
      return {
        start: min || days.today,
        end: max || days.today,
      }
    case CALENDAR_PRESETS.CUSTOM:
      return {
        start: days.today,
        end: days.today,
      }
    default:
      return {
        start: days.last7daysStart,
        end: days.today,
      }
  }
}

export const getDuration = (preset, start, end) =>
  preset.duration || end.diff(start, 'days') + 1

export const getDisplayText = (
  presetValue,
  startString,
  endString,
  minString,
  maxString,
  duration
) => {
  const min = minString ? moment(minString) : null
  const max = maxString ? moment(maxString) : null
  const { start: presetStart, end: presetEnd } = getPresetRange(
    presetValue,
    min,
    max
  )
  const displayStart = moment(startString)
  const displayEnd = moment(endString)
  const days = getPresetDays(min, max)
  if (
    presetValue !== CALENDAR_PRESETS.CUSTOM &&
    presetStart.isSame(displayStart, 'days') &&
    presetEnd.isSame(displayEnd, 'days')
  ) {
    return presetValue.text
  } else if (
    presetValue === CALENDAR_PRESETS.TODAY ||
    presetValue === CALENDAR_PRESETS.YESTERDAY
  ) {
    if (
      displayStart.isSame(days.today, 'days') &&
      displayEnd.isSame(days.today, 'days')
    ) {
      return CALENDAR_PRESETS.TODAY.text
    } else if (
      displayStart.isSame(days.yesterday, 'days') &&
      displayEnd.isSame(days.yesterday, 'days')
    ) {
      return CALENDAR_PRESETS.YESTERDAY.text
    } else {
      return `${moment().diff(displayStart, 'days')}일 전`
    }
  } else if (
    presetValue === CALENDAR_PRESETS.THIS_WEEK ||
    presetValue === CALENDAR_PRESETS.LAST_WEEK
  ) {
    if (
      displayStart.isSame(days.thisWeekStart, 'days') &&
      displayEnd.isSame(days.thisWeekEnd, 'days')
    ) {
      return CALENDAR_PRESETS.THIS_WEEK.text
    } else if (
      displayStart.isSame(days.lastWeekStart, 'days') &&
      displayEnd.isSame(days.lastWeekEnd, 'days')
    ) {
      return CALENDAR_PRESETS.LAST_WEEK.text
    } else {
      return `${moment().diff(displayStart, 'weeks')}주 전`
    }
  } else if (
    presetValue === CALENDAR_PRESETS.THIS_MONTH ||
    presetValue === CALENDAR_PRESETS.LAST_MONTH
  ) {
    if (
      displayStart.isSame(days.thisMonthStart, 'days') &&
      displayEnd.isSame(days.thisMonthEnd, 'days')
    ) {
      return CALENDAR_PRESETS.THIS_MONTH.text
    } else if (
      displayStart.isSame(days.lastMonthStart, 'days') &&
      displayEnd.isSame(days.lastMonthEnd, 'days')
    ) {
      return CALENDAR_PRESETS.LAST_MONTH.text
    } else {
      return `${moment().diff(displayStart, 'months')}개월 전`
    }
  } else {
    return `${duration}일 단위`
  }
}
